import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Params} from '@angular/router';
import {PlugStockService} from '../plug-stock.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {PlugStock} from '../plugStock';
import {BehaviorSubject, Observable} from 'rxjs';
import {Location} from '@angular/common';
import {VoorraadDeleteDialogComponent} from '../../pot/pot-detail/delete-dialog/voorraad-delete-dialog.component';
import {DeleteDialogResult} from '../../pot/pot-detail/delete-dialog/DeleteDialogResult';
import {ChangeVrijDialogComponent} from '../../pot/pot-detail/change-vrij-dialog/change-vrij-dialog.component';
import {map, share} from 'rxjs/operators';
import {ReservedSalesStock} from '../../../reservation/reservedStock';
import {ReservationService} from '../../../reservation/reservation.service';
import {MoveStockDialog} from '../../move-stock-dialog/move-stock-dialog';
import {SplitDialogComponent} from '../../pot/pot-detail/split-dialog/split-dialog.component';
import {UnitSize} from '../../../common/unitsize-autocomplete-field/unitsize';

@Component({
  selector: 'app-plug-stock-detail',
  templateUrl: './plug-stock-detail.component.html',
  styleUrls: ['./plug-stock-detail.component.scss']
})
export class PlugStockDetailComponent implements OnInit {

  private readonly googleSearchUrl = 'https://www.google.com/search?tbm=isch&q=';

  originalLocation: string;
  loadingSubject = new BehaviorSubject<boolean>(true);
  isLoading$: Observable<boolean> = this.loadingSubject.asObservable();
  original: PlugStock;
  error: any;
  item: PlugStock;
  reservation$: Observable<ReservedSalesStock[]>;

  constructor(
    private stekService: PlugStockService,
    private route: ActivatedRoute,
    private reservationService: ReservationService,
    private _location: Location,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
  }

  ngOnInit() {
    this.route.params.subscribe((params: Params) => {
      const key: number = +params['id'];
      this.stekService.getById(key)
        .subscribe(v => {
            this.original = v;
            this.item = this.original;
            this.originalLocation = v.location;
            this.loadingSubject.next(false);
            if (this.item.hasReservations) {
              this.reservation$ =
                this.reservationService.getReservedSalesStockByStockId(this.item.id)
                  .pipe(share());
            }
          },
          error => {
            this.error = error;
          });
    });
  }

  searchForImages() {
    window.open(this.googleSearchUrl + this.item.plantName, '_blank');
  }

  cancel() {
    this.item = this.original;
    this._location.back();
  }

  applyChanges() {
    this.validateAndSaveChanges()
      .then(plugStock => {
      this.item = plugStock;
    })
      .catch(err => {
        console.error(err);
        this.snackBar.open('Kon voorraadpartij niet opslaan', 'sluit', {duration: 2000});
      });
  }

  saveAndBack() {
    this.validateAndSaveChanges().then(_ => this._location.back());
  }

  calculateRestAndTotal() {
    const originalAmount = this.item.originalAmount || 0;
    const dead = this.item.dead || 0;
    const amountTrashed = this.item.amountTrashed || 0;
    const sold = this.item.sold || 0;
    const amountReserved = this.item.amountReserved || 0;
    const amountReservedForProduction = this.item.amountReservedForProduction || 0;
    const amountPotted = this.item.pottedAmount || 0;
    this.item.rest = originalAmount - dead - amountTrashed - sold - amountReserved - amountReservedForProduction - amountPotted;
    this.item.total = originalAmount - dead - amountTrashed - sold - amountPotted;
  }

  private registerAllDead() {
    this.item.dead = (this.item.dead || 0) + this.item.rest;
    this.item.rest = 0;
    this.item.amountFitForSale = 0;
    this.saveAndBack();
  }

  private registerNotSellable() {
    this.item.amountReserved = (this.item.amountReserved || 0) + this.item.rest;
    this.item.rest = 0;
    this.item.amountFitForSale = 0;
    this.saveAndBack();
  }

  openDeleteDialog() {
    if (this.item) {
      this.dialog.open(VoorraadDeleteDialogComponent)
        .afterClosed().toPromise().then((result: DeleteDialogResult) => {
        switch (result) {
          case DeleteDialogResult.DOOD:
            this.registerAllDead();
            break;
          case DeleteDialogResult.UNSELLABLE:
            this.registerNotSellable();
            break;
        }
      });
    }
  }

  private async validateAndSaveChanges(): Promise<PlugStock> {
    try {
      this.loadingSubject.next(true);
      if (!await this.checkForMoreVoorraadInSameBed()) {
        return Promise.reject();
      }
      if (!await this.checkVrijMoreThanRest()) {
        return Promise.reject();
      }
      this.setLastCountedDate();
      this.setAskedForCounting();
      return await this.stekService.update(this.item).toPromise();
    } catch (error) {
      console.error('Error while saving voorraad', error);
      this.snackBar.open('Kon voorraadpartij niet opslaan', 'sluit', {duration: 2000});
      throw error;
    } finally {
      this.loadingSubject.next(false);
    }
  }

  private checkVrijMoreThanRest(): Promise<boolean> {
    if ((this.item.amountFitForSale || 0) <= (this.item.rest || 0)) {
      return Promise.resolve(true);
    } else {
      return this.dialog.open(ChangeVrijDialogComponent, {data: this.item})
        .afterClosed().pipe(map(i => !!i)).toPromise();
    }
  }

  private async checkForMoreVoorraadInSameBed(): Promise<boolean> {
    if (this.originalLocation !== this.item.location) {
      const moreOfSameVoorraad: PlugStock[] = (await this.stekService.findByLocation(this.originalLocation).toPromise())
        .filter(v => v.plantCode === this.item.plantCode)
        .filter(v => v.id !== this.item.id);
      if (moreOfSameVoorraad.length > 0) {
        return this.dialog.open(MoveStockDialog, {data: [moreOfSameVoorraad, this.item.location]})
          .afterClosed().toPromise();
      }
    }
    return Promise.resolve(true);
  }

  private setLastCountedDate() {
    this.item.lastInspection = new Date();
  }

  private setAskedForCounting() {
    this.item.askedToBeCounted = false;
  }

  splitItem() {
    return this.dialog.open(SplitDialogComponent, {data: this.item});
  }

  setUnitSize($event: UnitSize) {
    this.item.unitSizeName = $event.name;
    this.item.unitSizeId = $event.id;
  }
}
